/**
 * Utility functions for ImageGalleryCarouselDesktop component
 */

/**
 * Generates CSS classes for carousel wrapper based on state
 * @param {boolean} isActive - Whether the carousel item is active
 * @param {boolean} isVideo - Whether the carousel item is a video
 * @returns {string} - Combined CSS classes
 */
export const getCarouselWrapperClasses = (isActive, isVideo) => {
  let classes = 'carousel-wrapper';

  if (isActive) {
    classes += ' active';
  }

  if (isVideo) {
    classes += ' video';
  }

  return classes;
};

/**
 * Generates CSS classes for indicator dots based on state
 * @param {boolean} isActive - Whether the indicator is active
 * @param {boolean} isRebranded - Whether to apply rebranded styling
 * @returns {string} - Combined CSS classes
 */
export const getIndicatorDotClasses = (isActive, isRebranded = true) => {
  let classes = 'dot';

  if (isActive) {
    classes += ' dot-active';
  }

  if (isRebranded) {
    classes += ' rebranded';
  }

  return classes;
};

/**
 * Generates unique key for carousel items
 * @param {string} mediaId - Media ID from image data
 * @param {number} index - Array index
 * @returns {string} - Unique key
 */
export const getCarouselItemKey = (mediaId, index) => {
  return `${mediaId}-${index}`;
};

/**
 * Generates ID for carousel items
 * @param {number} index - Array index
 * @returns {string} - Element ID
 */
export const getCarouselItemId = (index) => {
  return `PDP_swipe_photo_${index + 1}`;
};

/**
 * Generates ID for indicator dots
 * @param {number} index - Array index
 * @returns {string} - Element ID
 */
export const getIndicatorDotId = (index) => {
  return `ImageGalleryCarouselDesktop-dot-${index}`;
};

/**
 * Generates alt text for images
 * @param {string} brandName - Brand name
 * @param {string} productName - Product name
 * @returns {string} - Alt text
 */
export const getImageAltText = (brandName, productName) => {
  return `${brandName} - ${productName}`;
};

/**
 * Calculates whether indicators should be shown
 * @param {number} totalImages - Total number of images
 * @param {number} imagesPerSlide - Images per slide
 * @returns {boolean} - Whether to show indicators
 */
export const shouldShowIndicators = (totalImages, imagesPerSlide) => {
  return Math.ceil(totalImages / imagesPerSlide) > 1;
};

/**
 * Calculates total number of indicator dots needed
 * @param {number} totalImages - Total number of images
 * @param {number} imagesPerSlide - Images per slide
 * @returns {number} - Number of indicator dots
 */
export const calculateTotalIndicators = (totalImages, imagesPerSlide) => {
  return Math.ceil(totalImages / imagesPerSlide);
};

/**
 * Calculates the active image index based on indicator and position
 * @param {number} indicatorIndex - Current indicator index
 * @param {number} imagesPerSlide - Images per slide
 * @param {number} positionIndex - Position within the slide
 * @returns {number} - Calculated active image index
 */
export const calculateActiveImageIndex = (indicatorIndex, imagesPerSlide, positionIndex) => {
  return indicatorIndex * imagesPerSlide + positionIndex;
};

/**
 * Checks if a carousel item is active based on current state
 * @param {number} activeImageIndex - Current active image index
 * @param {number} imagesPerSlide - Images per slide
 * @param {number} itemIndex - Index of the item to check
 * @returns {boolean} - Whether the item is active
 */
export const isCarouselItemActive = (activeImageIndex, imagesPerSlide, itemIndex) => {
  return activeImageIndex % imagesPerSlide === itemIndex;
};
